// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import isApiError from "@insite/client-framework/Common/isApiError";
import { get, post, ServiceResult } from "@insite/client-framework/Services/ApiService";
import { CreateQuoteApiParameter, UpdateQuoteApiParameter } from "@insite/client-framework/Services/QuoteService";
import { QuoteModel } from "@insite/client-framework/Types/ApiModels";

const vmiQuotesUrl = "/api/vmi/quote/rejectquote";
const quotesUrl = "/api/v1/quotes";

export async function updateQuote(parameter: UpdateQuoteApiParameter): Promise<ServiceResult<QuoteModel>> {
    try {
        const quote = await get<QuoteModel>(`${vmiQuotesUrl}`, parameter);
        cleanQuote(quote);
        return {
            successful: true,
            result: quote,
        };
    } catch (error) {
        if (isApiError(error) && error.status === 400) {
            return {
                successful: false,
                errorMessage: error.errorJson.message,
            };
        }
        throw error;
    }
}

function cleanQuote(quote: QuoteModel) {
    quote.orderDate = quote.orderDate ? new Date(quote.orderDate) : null;
    quote.expirationDate = quote.expirationDate ? new Date(quote.expirationDate) : null;
    quote.cartLines = quote.quoteLineCollection;
    quote.messageCollection?.forEach(message => {
        if (message.createdDate) {
            message.createdDate = new Date(message.createdDate);
        }
    });
}

export interface xCreateQuoteApiParameter extends CreateQuoteApiParameter {
    poNumber: string;
}

export async function xCreateQuote(createQuoteParameter: xCreateQuoteApiParameter): Promise<ServiceResult<QuoteModel>> {
    try {
        // xng-changes: append poNumber as query param if provided
        const url = createQuoteParameter.poNumber?.trim()
            ? `${quotesUrl}?poNumber=${encodeURIComponent(createQuoteParameter.poNumber)}`
            : quotesUrl;

        const quote = await post<CreateQuoteApiParameter, QuoteModel>(url, createQuoteParameter);
        return {
            successful: true,
            result: quote,
        };
    } catch (error) {
        if (isApiError(error) && error.status === 400) {
            return {
                successful: false,
                errorMessage: error.errorJson.message,
            };
        }
        throw error;
    }
}
